// src/components/product/BrandSelector/BrandSelector.jsx
import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Search, X, Building2, Plus } from 'lucide-react';
import { getBrands, createBrand } from '../../../services/api/brands';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { useAlert } from '../../../contexts/AlertContext';
import styles from './BrandSelector.module.css';

const BrandSelector = ({ 
  selectedBrandId = null, 
  onBrandChange, 
  placeholder = "Select brand",
  error = null,
  allowCreateNew = true
}) => {
  const { t } = useLocalization();
  const { success, error: showError } = useAlert();
  const [isOpen, setIsOpen] = useState(false);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newBrandName, setNewBrandName] = useState('');
  const [newBrandDescription, setNewBrandDescription] = useState('');
  const [createBrandError, setCreateBrandError] = useState('');
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const createInputRef = useRef(null);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setShowCreateForm(false);
        setNewBrandName('');
        setNewBrandDescription('');
        setCreateBrandError('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load brands when component mounts
  useEffect(() => {
    if (brands.length === 0) {
      loadBrands();
    }
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current && !showCreateForm) {
      setTimeout(() => {
        searchInputRef.current.focus();
      }, 100);
    }
  }, [isOpen, showCreateForm]);

  // Focus create input when create form shows
  useEffect(() => {
    if (showCreateForm && createInputRef.current) {
      setTimeout(() => {
        createInputRef.current.focus();
      }, 100);
    }
  }, [showCreateForm]);

  const loadBrands = async () => {
    try {
      setLoading(true);
      const response = await getBrands();
      setBrands(response.brands || []);
    } catch (error) {
      console.error('Error loading brands:', error);
      setBrands([]);
    } finally {
      setLoading(false);
    }
  };

  const handleBrandSelect = (brandId) => {
    onBrandChange(brandId);
    setIsOpen(false);
  };

  const handleSelectorClick = () => {
    setIsOpen(!isOpen);
  };

  const handleCreateNewClick = () => {
    setShowCreateForm(true);
    setSearchTerm('');
    setCreateBrandError('');
  };

  const handleCancelCreate = () => {
    setShowCreateForm(false);
    setNewBrandName('');
    setNewBrandDescription('');
    setCreateBrandError('');
  };

  const validateBrandName = (name) => {
    if (!name.trim()) {
      return t('brands.nameRequired');
    }
    if (name.trim().length < 2) {
      return t('brands.nameMinLength');
    }
    
    // Check if brand already exists
    const existingBrand = brands.find(brand => 
      brand.name.toLowerCase() === name.trim().toLowerCase()
    );
    if (existingBrand) {
      return 'Brand with this name already exists';
    }
    
    return null;
  };

  const handleCreateBrand = async () => {
    const validationError = validateBrandName(newBrandName);
    if (validationError) {
      setCreateBrandError(validationError);
      return;
    }

    setIsCreating(true);
    setCreateBrandError('');

    try {
      const brandData = new FormData();
      brandData.append('name', newBrandName.trim());
      if (newBrandDescription.trim()) {
        brandData.append('description', newBrandDescription.trim());
      }

      const response = await createBrand(brandData);
      
      // Add the new brand to the brands list
      const newBrand = response.brand;
      setBrands(prev => [...prev, newBrand]);
      
      // Automatically select the new brand
      onBrandChange(newBrand.id);
      
      // Reset form
      setNewBrandName('');
      setNewBrandDescription('');
      setShowCreateForm(false);
      setIsOpen(false);
      
      // Show success message
      success(t('brands.brandCreatedSuccess', { brandName: newBrand.name }), {
        title: t('common.success'),
        duration: 3000
      });
      
    } catch (error) {
      console.error('Error creating brand:', error);
      if (error.response?.data?.message) {
        setCreateBrandError(error.response.data.message);
      } else {
        setCreateBrandError(t('brands.failedToCreate'));
      }
    } finally {
      setIsCreating(false);
    }
  };

  const handleCreateInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCreateBrand();
    } else if (e.key === 'Escape') {
      handleCancelCreate();
    }
  };

  const filteredBrands = brands.filter(brand =>
    brand.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (brand.description && brand.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const selectedBrand = brands.find(brand => brand.id === selectedBrandId);

  // Load brands if they haven't been loaded yet and we have a selected brand ID
  useEffect(() => {
    if (selectedBrandId && brands.length === 0) {
      loadBrands();
    }
  }, [selectedBrandId, brands.length]);

  // Check if search term could be a new brand
  const canCreateFromSearch = allowCreateNew && 
    searchTerm.trim() && 
    !filteredBrands.some(brand => brand.name.toLowerCase() === searchTerm.toLowerCase());

  return (
    <div className={styles.brandSelector} ref={dropdownRef}>
      <div 
        className={`${styles.selectorInput} ${error ? styles.error : ''} ${isOpen ? styles.open : ''}`}
        onClick={handleSelectorClick}
      >
        <div className={styles.selectedContent}>
          {selectedBrand ? (
            <div className={styles.selectedBrand}>
              <Building2 size={16} className={styles.brandIcon} />
              <span className={styles.brandText}>{selectedBrand.name}</span>
            </div>
          ) : (
            <span className={styles.placeholder}>{placeholder}</span>
          )}
        </div>
        <ChevronDown 
          size={18} 
          className={`${styles.chevron} ${isOpen ? styles.rotated : ''}`} 
        />
      </div>

      {error && <span className={styles.errorText}>{error}</span>}

      {isOpen && (
        <div className={styles.dropdown}>
          {!showCreateForm ? (
            <>
              {/* Search Input */}
              <div className={styles.searchContainer}>
                <Search size={16} className={styles.searchIcon} />
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search brands..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={styles.searchInput}
                />
              </div>

              {/* Create New Brand from Search */}
              {canCreateFromSearch && (
                <div className={styles.createFromSearch}>
                  <button
                    type="button"
                    className={styles.createFromSearchButton}
                    onClick={() => {
                      setNewBrandName(searchTerm);
                      setShowCreateForm(true);
                    }}
                  >
                    <Plus size={16} />
                    <span>Create brand: "{searchTerm}"</span>
                  </button>
                </div>
              )}

              {/* None Option */}
              <div className={styles.brandsList}>
                <BrandItem
                  brand={null}
                  isSelected={selectedBrandId === null}
                  onSelect={() => handleBrandSelect(null)}
                />
                
                {loading ? (
                  <div className={styles.loadingState}>
                    <div className={styles.loadingSpinner}></div>
                    <span>Loading brands...</span>
                  </div>
                ) : filteredBrands.length > 0 ? (
                  filteredBrands.map((brand) => (
                    <BrandItem
                      key={brand.id}
                      brand={brand}
                      isSelected={selectedBrandId === brand.id}
                      onSelect={() => handleBrandSelect(brand.id)}
                    />
                  ))
                ) : (
                  <div className={styles.emptyState}>
                    {searchTerm ? 'No brands found matching your search' : 'No brands available'}
                  </div>
                )}
              </div>

              {/* Create New Brand Button */}
              {allowCreateNew && (
                <div className={styles.createNewSection}>
                  <button
                    type="button"
                    className={styles.createNewButton}
                    onClick={handleCreateNewClick}
                  >
                    <Plus size={16} />
                    Create New Brand
                  </button>
                </div>
              )}
            </>
          ) : (
            /* Create Brand Form */
            <div className={styles.createBrandForm}>
              <div className={styles.createFormHeader}>
                <h3>Create New Brand</h3>
                <button
                  type="button"
                  className={styles.createFormClose}
                  onClick={handleCancelCreate}
                >
                  <X size={16} />
                </button>
              </div>
              
              <div className={styles.createForm}>
                <div className={styles.createFormGroup}>
                  <label htmlFor="newBrandName">Brand Name *</label>
                  <input
                    ref={createInputRef}
                    type="text"
                    id="newBrandName"
                    value={newBrandName}
                    onChange={(e) => {
                      setNewBrandName(e.target.value);
                      setCreateBrandError('');
                    }}
                    onKeyDown={handleCreateInputKeyDown}
                    placeholder="Enter brand name"
                    className={createBrandError ? styles.errorInput : ''}
                    disabled={isCreating}
                  />
                  {createBrandError && (
                    <span className={styles.createErrorText}>{createBrandError}</span>
                  )}
                </div>

                <div className={styles.createFormGroup}>
                  <label htmlFor="newBrandDescription">Description (Optional)</label>
                  <textarea
                    id="newBrandDescription"
                    value={newBrandDescription}
                    onChange={(e) => setNewBrandDescription(e.target.value)}
                    placeholder="Enter brand description"
                    disabled={isCreating}
                    rows={3}
                  />
                </div>
                
                <div className={styles.createFormActions}>
                  <button
                    type="button"
                    className={styles.createCancelButton}
                    onClick={handleCancelCreate}
                    disabled={isCreating}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className={styles.createSubmitButton}
                    onClick={handleCreateBrand}
                    disabled={isCreating || !newBrandName.trim()}
                  >
                    {isCreating ? (
                      <>
                        <div className={styles.createLoadingSpinner}></div>
                        Creating...
                      </>
                    ) : (
                      <>
                        <Plus size={16} />
                        Create Brand
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const BrandItem = ({ brand, isSelected, onSelect }) => {
  return (
    <div 
      className={`${styles.brandItem} ${isSelected ? styles.selected : ''}`}
      onClick={onSelect}
    >
      <div className={styles.brandContent}>
        <Building2 size={16} className={styles.brandIcon} />
        <div className={styles.brandInfo}>
          <span className={styles.brandName}>
            {brand ? brand.name : 'None'}
          </span>
          {brand && brand.description && (
            <span className={styles.brandDescription}>{brand.description}</span>
          )}
        </div>
      </div>
      
      <div className={styles.radioButton}>
        <input
          type="radio"
          checked={isSelected}
          onChange={onSelect}
          onClick={(e) => e.stopPropagation()}
        />
      </div>
    </div>
  );
};

export default BrandSelector;